/**
 * RelayNetworkDebug adapted from https://github.com/facebook/relay/blob/v1.7.0/packages/react-relay/classic/tools/RelayNetworkDebug.js
 *
 * Copyright (c) 2013-present, Facebook, Inc.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 * Modified to interface directly with Bugsnag for Buildkite.
 *
 * @flow
 */

import * as Relay from 'react-relay/classic';
import Bugsnag from 'app/lib/Bugsnag';
import type { Variables } from 'relay-runtime';

/**
 * Ported from https://github.com/facebook/fbjs/blob/v0.8.17/src/performance/performanceNow.js
 *
 * Uses the same license as react-relay.
 */
const performanceNow = ((performance) => (
  performance && performance.now
    ? () => performance.now()
    : () => Date.now()
))(window.performance || window.msPerformance || window.webkitPerformance);

/**
 * Ported from https://github.com/facebook/fbjs/blob/v0.8.17/src/request/xhrSimpleDataSerializer.js
 *
 * Uses the same license as react-relay.
 */
function xhrSimpleDataSerializer(data) {
  const uri = [];
  let key;
  for (key in data) {
    uri.push(encodeURIComponent(key) + '=' + encodeURIComponent(data[key]));
  }
  return uri.join('&');
}

/**
 * Type information ported from https://github.com/facebook/relay/blob/v1.7.0/packages/react-relay
 *
 * These types distill all the information that this file touches, rather than
 * including the full APIs. We probably don't want to touch any more of this
 * than we already do, so I feel like this is reasonable.
 *
 * (The npm version of react-relay does not include any type information, but
 * additionally this is mostly internal stuff that I just want typed as they
 * do so that I know I'm not breaking anything)
 */
type QueryPayload = {[key: string]: mixed};

type QueryResult = {
  error?: ?Error,
  ref_params?: ?{[name: string]: mixed},
  response: QueryPayload
};

type MutationResult = {
  response: QueryPayload
};

declare class Deferred<Tresult> {
  getPromise: () => Promise<Tresult>
}

declare class RelayRequest<Tresult> extends Deferred<Tresult> {
  getDebugName: () => string,
  getVariables: () => Variables,
  getQueryString: () => string
}

declare class RelayQueryRequest extends RelayRequest<QueryResult> {}
declare class RelayMutationRequest extends RelayRequest<MutationResult> {}

type MutationCallback = (request: RelayMutationRequest) => void;
type QueryCallback = (request: RelayQueryRequest) => void;

declare class RelayEnvironment {
  addNetworkSubscriber: (
    queryCallback?: ?QueryCallback,
    mutationCallback?: ?MutationCallback,
  ) => ChangeSubscription
}

type ChangeSubscription = {
  remove(): void
};

type RelayNetworkDebuggable = {
  name: string,
  type: string,
  promise: Promise<any>,
  logResult: (error: ?Object, response: ?Object) => void
};

class RelayNetworkDebugger {
  _initTime: number;
  _queryID: number;
  _subscription: ChangeSubscription;

  constructor(environment: RelayEnvironment) {
    this._initTime = performanceNow();
    this._queryID = 0;
    this._subscription = environment.addNetworkSubscriber(
      (request) => this.logRequest(
        createDebuggableFromRequest('Relay Query', request)
      ),
      (request) => this.logRequest(
        createDebuggableFromRequest('Relay Mutation', request)
      )
    );
  }

  uninstall(): void {
    this._subscription.remove();
  }

  logRequest({ name, type, promise, logResult }: RelayNetworkDebuggable): void {
    const id = this._queryID++;
    const requestStart = performanceNow();

    const onSettled = (error, response) => {
      const durationMilliseconds = performanceNow() - requestStart;

      Bugsnag.leaveBreadcrumb(
        `Relay Classic request ${error ? 'failed' : 'succeeded'}`,
        {
          id,
          type,
          name,
          durationMilliseconds
        },
        'request'
      );

      logResult(error, response);
    };

    promise.then(
      (response) => onSettled(null, response),
      (error) => onSettled(error, null)
    );
  }
}

function createDebuggableFromRequest(
  type: string,
  request: RelayQueryRequest | RelayMutationRequest
): RelayNetworkDebuggable {
  return {
    name: request.getDebugName(),
    type,
    promise: request.getPromise(),
    logResult(error, response) {
      const queryText = request.getQueryString();
      const requestVariables = request.getVariables();

      const requestSize = formatSize(
        xhrSimpleDataSerializer({
          'q': queryText,
          query_params: requestVariables
        }).length
      );

      Bugsnag.leaveBreadcrumb(
        `Relay Classic result ${error ? 'failed' : 'succeeded'}`,
        {
          type,
          requestSize,
          queryText,
          error: error ? (error.stack || error.toString()) : error,
          response
        },
        'request'
      );
    }
  };
}

const ALL_UNITS = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
function formatSize(bytes: number): string {
  const sign = bytes < 0 ? -1 : 1;
  bytes = Math.abs(bytes);
  let idx = 0;
  while (bytes >= Math.pow(1024, idx + 1) && idx < ALL_UNITS.length) {
    idx++;
  }
  const value = (sign * bytes * 1.0) / Math.pow(1024, idx);
  return Number(value.toFixed(2)) + ALL_UNITS[idx];
}

let networkDebugger: ?RelayNetworkDebugger;

const RelayNetworkDebug = {
  init(
    environment: RelayEnvironment = (Relay.Store: any)
  ) {
    networkDebugger && networkDebugger.uninstall();
    networkDebugger = new RelayNetworkDebugger(environment);
  },

  logRequest(request: RelayNetworkDebuggable) {
    networkDebugger && networkDebugger.logRequest(request);
  }
};

export default RelayNetworkDebug;
